"use client";

import React, { createContext, useContext, useEffect, useState } from "react";
import Image from "next/image";
import { AnimatePresence, MotionDiv } from "@/components/ui/MotionWrapper";

const LoadingContext = createContext(null);

export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error("useLoading must be used within a LoadingProvider");
  }
  return context;
};

export const LoadingProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isClient, setIsClient] = useState(false);
  const [contentReady, setContentReady] = useState(false);

  useEffect(() => {
    setIsClient(true);

    // Show initial loader for 2 seconds
    const timer = setTimeout(() => {
      setIsInitialLoad(false);
      setIsLoading(false);
    }, 2000);

    setTimeout(() => {
      setContentReady(true);
    }, 200)

    return () => {
      clearTimeout(timer);
    };
  }, []);

  const startLoading = (message = "Loading...") => {
    setLoadingMessage(message);
    setIsLoading(true);
  };

  const stopLoading = () => {
    setIsLoading(false);
    setLoadingMessage("");
  };

  const shouldShowLoader = isClient && (isInitialLoad || isLoading);

  return (
    <LoadingContext.Provider
      value={{
        isLoading,
        loadingMessage,
        startLoading,
        stopLoading,
        isInitialLoad,
        contentReady,
      }}
    >
      {contentReady && children}

      <AnimatePresence mode="wait">
        {shouldShowLoader && (
          <MotionDiv
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.1, 0, 0.2, 1] }}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/10 backdrop-blur-lg"
          >
            <div className="text-center relative z-10 max-w-md mx-auto px-6">
              <MotionDiv
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{
                  duration: 0.5,
                  delay: 0.1,
                  ease: [0.4, 0, 0.2, 1],
                }}
                className="mb-8"
              >
                <div className="relative w-28 h-28 mx-auto mb-6">
                  <MotionDiv
                    className="w-full h-full rounded-3xl flex items-center justify-center shadow-2xl p-4 bg-white  "
                    whileHover={{ scale: 1.02 }}
                  >
                    <Image
                      src="/images/logo.webp"
                      alt="SCC Infotech4"
                      width={100}
                      height={100}
                      loading="eager"
                      priority
                      placeholder="blur"
                      blurDataURL="/images/logo.webp"
                      className="object-contain"
                    />
                  </MotionDiv>

                  <MotionDiv
                    className="absolute inset-0 rounded-3xl border-2 border-[#8339EA]"
                    animate={{ rotate: 360 }}
                    transition={{
                      duration: 4,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                  />

                  <MotionDiv
                    className="absolute inset-2 rounded-2xl border-2 border-[#8997FB]"
                    animate={{ rotate: -360 }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                  />
                </div>
              </MotionDiv>
            </div>
          </MotionDiv>
        )}
      </AnimatePresence>
    </LoadingContext.Provider>
  );
};